// Staff Requisitions (internal) — /api/hr/requisitions

// Confirmed 2026-07-08 against the actual backend enum (@Enumerated(EnumType.STRING)):
// these five are the only values that have ever been persisted. CONSUMABLE/ASSET/STATIONERY
// were a frontend-only invention with no backend counterpart — there is no "stationery" split
// at the requisition-type level, only at the line itemType level (see ProcurementItemType).
export type RequisitionType =
  // Internal workflow (Dept Head, then Store Officer, approves/rejects)
  | "GOODS"
  | "ASSET_REQUEST"
  // External procurement workflow (multi-stage)
  | "SERVICE"
  | "WORKS"
  | "GENERAL";

/** Returns true for types that use the multi-stage external procurement workflow */
export function isExternalWorkflow(type?: RequisitionType | string | null): boolean {
  return type === "SERVICE" || type === "WORKS" || type === "GENERAL";
}

/** Returns true for GOODS requisitions — fulfilled by issuing inventory stock. */
export function isGoodsRequisition(type?: RequisitionType | string | null): boolean {
  return type === "GOODS";
}

/** Returns true for ASSET_REQUEST requisitions — fulfilled by assigning a physical asset. */
export function isAssetRequisition(type?: RequisitionType | string | null): boolean {
  return type === "ASSET_REQUEST";
}

// Confirmed 2026-07-08 — the real backend line-level item classification enum. No STATIONERY,
// no bare ASSET (only CAPITAL_ASSET); WORKS added for WORKS-type requisition lines.
export type ProcurementItemType =
  | "CONSUMABLE"
  | "CAPITAL_ASSET"
  | "SERVICE"
  | "WORKS";

export type HrRequisitionStatus =
  // Common
  | "DRAFT"
  | "SUBMITTED"
  | "PENDING_APPROVAL"   // legacy alias for SUBMITTED
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED"
  // Internal workflow terminal states
  | "PURCHASED"
  | "CLOSED"
  // Legacy fulfillment statuses
  | "FULFILLED"
  | "PARTIAL"
  // External procurement workflow stages
  | "QUOTATION_REQUESTED"
  | "QUOTATIONS_RECEIVED"
  | "BUDGET_APPROVED"
  | "BUDGET_REJECTED"
  | "EVALUATED";

// NOT_APPLICABLE is the real value RequisitionFulfillmentStatus returns for SERVICE/WORKS/
// GENERAL requisitions (anything that isn't physically fulfilled via stock/asset) — confirmed
// 2026-07-08. Without it, that case falls through as an unhandled/undefined status in the UI.
export type LineFulfillmentStatus = "PENDING" | "PARTIAL" | "FULFILLED" | "NOT_APPLICABLE";

export type HrRequisitionLine = {
  id?: number;
  lineOrder: number;
  description: string;
  quantity: number;
  unitOfMeasure?: string | null;
  // V69 cross-module fields
  itemType?: ProcurementItemType | null;
  inventoryItemId?: number | null;
  inventoryItemName?: string | null;
  assetCategoryId?: number | null;
  assetCategoryName?: string | null;
  approvedQuantity?: number | null;
  issuedQuantity?: number | null;
  fulfillmentStatus?: LineFulfillmentStatus | null;
};

// PUT /{id}/lines/{lineId} only accepts these three — classification fields
// (itemType/inventoryItemId/assetCategoryId) are create-only (POST /{id}/lines), confirmed
// 2026-07-08. There is currently no way to reclassify an existing line via any endpoint.
export type HrRequisitionLineUpdateRequest = {
  lineOrder?: number;
  description?: string;
  quantity?: number;
};

export type HrRequisitionDocument = {
  id: number;
  documentType?: string | null;
  fileName: string;
  uploadedAt?: string;
};

export type RequisitionActionLog = {
  id: number;
  action: string;
  performedByUsername?: string | null;
  performedByName?: string | null;
  notes?: string | null;
  // Observed 2026-07-09 — the CREATE row on at least some requisitions comes back with this
  // missing/null, unlike later workflow-transition rows. Treat as optional; render code must
  // not assume it's always a parseable string.
  performedAt?: string | null;
};

export type HrRequisition = {
  id: number;
  serialNo?: string;
  requisitionType?: RequisitionType | null;
  /** Legacy field — same as requisitionType */
  type?: RequisitionType | null;
  status: HrRequisitionStatus;
  purpose?: string | null;
  comments?: string | null;
  // Requester
  requesterEmployeeId?: number | null;
  requesterEmployeeNumber?: string | null;
  requesterName?: string | null;
  // Department
  departmentId?: number | null;
  departmentCode?: string | null;
  departmentName?: string | null;
  // Workflow metadata (from summary responses)
  workflowId?: number | null;
  workflowCode?: string | null;
  currentStepOrder?: number | null;
  currentStepName?: string | null;
  currentApproverUserId?: number | null;
  currentApproverUsername?: string | null;
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  cancelledAt?: string | null;
  // ── Procurement stage fields (external workflow only) ──────────────────────
  quotationRequestedAt?: string | null;
  procurementNotes?: string | null;
  quotationsReceivedAt?: string | null;
  budgetDecisionAt?: string | null;
  budgetDecisionNotes?: string | null;
  evaluatedAt?: string | null;
  evaluationNotes?: string | null;
  // Fulfillment
  fulfillmentStatus?: LineFulfillmentStatus | null;
  // V69 escalation — backend field is escalatedToPrNo
  escalatedToPrId?: number | null;
  escalatedToPrNo?: string | null;
  escalatedToPrSerialNo?: string | null;  // legacy alias
  // Backend added 2026-07-10 to RequisitionSummaryResponse (mine/inbox/pending-fulfillment) —
  // a cheap count for list rows that don't carry full `lines`.
  lineCount?: number;
  // Sub-lists — only actually populated on the detail endpoint (GET /{id}); list/summary
  // endpoints (mine/inbox/pending-fulfillment) omit this entirely despite the type not
  // marking it optional before 2026-07-10. Always guard with `.lines ?? []` / `.lines?.length`.
  lines?: HrRequisitionLine[];
  documents?: HrRequisitionDocument[];
  actionLogs?: RequisitionActionLog[];
};

export type HrRequisitionCreateRequest = {
  // Confirmed 2026-07-08 — @NotNull backend-side with no fallback (not inferred from the
  // requester's own record). Creation gets a 400 before business logic runs if this is omitted.
  departmentId: number;
  requisitionType?: RequisitionType;
  type?: RequisitionType;
  purpose?: string;
  lines?: Omit<HrRequisitionLine, "id" | "approvedQuantity" | "issuedQuantity" | "fulfillmentStatus">[];
};

// PUT /{id} only ever updates these three fields — it has no `lines` field at all, so sending
// lines there is silently ignored. Line edits go through the dedicated line endpoints below.
export type HrRequisitionUpdateRequest = {
  departmentId?: number;
  requisitionType?: RequisitionType;
  purpose?: string;
};

// ── Stock check ───────────────────────────────────────────────────────────────

export type StockCheckLine = {
  lineId: number;
  lineOrder: number;
  description: string;
  itemId?: number | null;
  itemName?: string | null;
  requestedQuantity: number;
  availableStock: number;
  canFulfill: boolean;
  shortfall: number;
};

// Observed 2026-07-10 — the backend can omit `lines` entirely (not just return an empty
// array), which crashed StockIssuePanel's unguarded `.some`/`.map` calls. Treat as optional
// everywhere it's consumed.
export type StockCheckResponse = {
  requisitionId: number;
  canFullyFulfill: boolean;
  lines?: StockCheckLine[] | null;
};

// ── Fulfillment ───────────────────────────────────────────────────────────────

// NOTE: field names must match the backend contract exactly — POST /{id}/issue-stock
// expects `issuances`/`quantityIssued`, not `lines`/`quantityToIssue`.
export type IssueStockLineRequest = { lineId: number; quantityIssued: number };
export type IssueStockRequest = { issuances: IssueStockLineRequest[]; notes?: string };

// Confirmed 2026-07-08 — backend's AssetAssignmentRequest.LineAssignment takes assetIds
// (plural, an array) per line, not a single assetId. A line with quantity > 1 can be
// fulfilled by assigning multiple distinct physical assets.
export type FulfillAssetLineRequest = { lineId: number; assetIds: number[] };
export type FulfillAssetRequest = { assignments: FulfillAssetLineRequest[]; notes?: string };

// GET /{id}/asset-availability returns a count summary per line, NOT actual pickable Asset
// records — confirmed 2026-07-08. There is no backend endpoint that returns "the actual
// asset list to pick from, filtered by this line's category"; the frontend gets real,
// pickable Asset records via assetApi.listAssets({ categoryId, status: "AVAILABLE" })
// instead, one call per line. This type is for the availability *count* only, used as a
// "3 available, need 2" hint alongside the real picker.
export type AssetAvailabilityLine = {
  lineId: number;
  assetCategoryId?: number | null;
  assetCategoryName?: string | null;
  requestedQuantity: number;
  availableCount: number;
  availability: "SUFFICIENT" | "INSUFFICIENT";
};

export type AssetAvailabilityResponse = {
  requisitionId: number;
  lines?: AssetAvailabilityLine[] | null;
};

export type EscalateToProcurementRequest = { purpose?: string; notes?: string };

// ── Queue items ───────────────────────────────────────────────────────────────

export type FulfillmentQueueItem = {
  id: number;
  serialNo?: string | null;
  requisitionType?: RequisitionType | null;
  type?: RequisitionType | null;
  status: HrRequisitionStatus;
  requesterName?: string | null;
  departmentName?: string | null;
  purpose?: string | null;
  submittedAt?: string | null;
  lineCount?: number;
};

export type RequisitionReportItem = {
  id: number;
  serialNo?: string | null;
  requisitionType?: RequisitionType | null;
  type?: RequisitionType | null;
  status: HrRequisitionStatus;
  requesterName?: string | null;
  departmentName?: string | null;
  purpose?: string | null;
  submittedAt?: string | null;
  closedAt?: string | null;
  lineCount?: number;
};

export type PageResponse<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
};
